import type { User } from '../types';

/**
 * 获取用户显示名称
 * 优先级: nickname > name的首字母大写
 */
export function getUserDisplayName(user: User): string {
  if (user?.nickname) {
    return user.nickname;
  }
  
  // 生成首字母大写的昵称作为默认值
  if (user?.name) {
    return user.name.charAt(0).toUpperCase() + user.name.slice(1).toLowerCase();
  }
  
  return '未知用户';
}

/**
 * 获取团队成员的显示名称，支持当前用户标识
 */
export function getTeamMemberDisplayName(member: { user?: User; userId: string }, currentUserId?: string): string {
  const displayName = member.user
    ? getUserDisplayName(member.user)
    : `用户${member.userId.slice(-4)}`;

  const isCurrentUser = member.userId === currentUserId;

  return isCurrentUser ? `${displayName} (我)` : displayName;
}

/**
 * 计算字符串的显示长度（中文字符按1计算，英文字符按0.5计算）
 * 用于统一的长度限制：中文不超过18个字，英文不超过36个字
 */
export function getDisplayLength(text: string): number {
  if (!text) return 0;

  let length = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    // 判断是否为中文字符（包括中文标点）
    if (/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3000-\u303f\uff00-\uffef]/.test(char)) {
      length += 1; // 中文字符计为1
    } else {
      length += 0.5; // 英文字符计为0.5
    }
  }
  return length;
}

/**
 * 检查字符串是否超过显示长度限制
 * @param text 要检查的文本
 * @param maxLength 最大长度（以中文字符为基准，默认18）
 */
export function isTextTooLong(text: string, maxLength: number = 18): boolean {
  return getDisplayLength(text) > maxLength;
}

/**
 * 截断文本到指定的显示长度
 * @param text 要截断的文本
 * @param maxLength 最大长度（以中文字符为基准，默认18）
 */
export function truncateText(text: string, maxLength: number = 18): string {
  if (!text) return '';

  let length = 0;
  let result = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charLength = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3000-\u303f\uff00-\uffef]/.test(char) ? 1 : 0.5;

    if (length + charLength > maxLength) {
      break;
    }

    length += charLength;
    result += char;
  }

  return result;
}

/**
 * Markdown → 卡片上的纯文字摘要：去掉标题井号、加粗、链接地址、表格竖线、代码围栏等符号，
 * 只留可读文字（完整排版在任务弹窗里看）
 */
export function markdownToPlain(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ')            // 代码块整段去掉
    .replace(/^\s*\|?[\s:|-]{3,}\|?\s*$/gm, '')  // 表格分隔行
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')         // 标题
    .replace(/^\s{0,3}>\s?/gm, '')              // 引用
    .replace(/^\s*[-*+]\s+(\[[ xX]\]\s+)?/gm, '· ') // 列表 / 任务项
    .replace(/^\s*\d+\.\s+/gm, '')              // 有序列表序号
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')    // 图片
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')    // 链接只留文字
    .replace(/(\*\*|__|~~|`)/g, '')              // 加粗 / 删除线 / 行内代码
    .replace(/\|/g, ' ')                         // 表格竖线
    .replace(/\n{2,}/g, '\n')
    .trim();
}
